import React from "react";
import {
  useController,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";
import {
  RadioProps,
  FormControlLabelProps,
  Typography,
  Box,
} from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { Option } from "@helpers/types";
import checkboxCheckedSvg from "@icons/myicons/checkbox-checked.svg";

const Error = styled.div`
  color: ${({ theme }) => theme.palette.error.main};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const CheckboxContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  cursor: "pointer",

  "&:hover .checkbox": {
    borderColor: theme.palette.primary.light,
  },
}));

const Checkbox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "checked",
})<{
  checked?: boolean;
}>(({ theme, checked }) => ({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  borderRadius: "4px",
  border: `2px solid ${
    checked ? theme.palette.primary.main : theme.palette.border.primary
  }`,
  backgroundColor: checked ? theme.palette.primary.main : "transparent",
  transition: "all 0.2s ease",

  ...(checked && {
    "&:after": {
      content: '""',
      display: "block",
      width: "12px",
      height: "12px",
      background: `no-repeat center center url(${checkboxCheckedSvg})`,
      backgroundSize: "contain",
    },
  }),
}));

export const RHFCheckboxItem = ({
  option,
  onChange,
  checked = false,
  translationNamespace,
}: {
  option: Option;
  onChange: (value: Option["value"]) => void;
  checked?: boolean;
  translationNamespace?: string;
}) => {
  const { t, i18n } = useTranslation();

  const label =
    option.label ||
    (i18n.exists(`${translationNamespace}.${option.value}`)
      ? t(`${translationNamespace}.${option.value}`)
      : option.value);

  return (
    <CheckboxContainer
      onClick={() => onChange(option.value)}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          onChange(option.value);
        }
      }}
      tabIndex={0}
      data-testid={option.testId}
      role="checkbox"
      aria-checked={checked}
      aria-label={typeof label === "string" ? label : undefined}
    >
      <Checkbox checked={checked} className="checkbox" />
      <Typography variant="body3" component="span">
        {label}
      </Typography>
    </CheckboxContainer>
  );
};

const CheckboxGroup = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(6),
}));

const InlineContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "inline",
})<{ inline?: boolean }>(({ theme, inline }) => ({
  display: "flex",
  flexDirection: inline ? "row" : "column",
  alignItems: inline ? "center" : "flex-start",
  gap: theme.spacing(inline ? 6 : 2),
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column",
    alignItems: "flex-start",
    gap: theme.spacing(2),
  },
}));

interface Props {
  name: string;
  label?: FormControlLabelProps["label"];
  options: Option[];
  testId?: string;
  multi?: boolean;
  inline?: boolean;
  translationNamespace?: string;
}

type FormInputProps<T extends FieldValues> = Omit<RadioProps, "size"> &
  Props &
  UseControllerProps<T>;

export const RHFCheckboxGroup = <T extends FieldValues>(
  props: FormInputProps<T>
): React.ReactNode => {
  const {
    control,
    name,
    defaultValue,
    label,
    options,
    testId,
    multi = false,
    inline = false,
    translationNamespace,
  } = props;

  const {
    field,
    fieldState: { error },
  } = useController<T>({ control, name, defaultValue });

  const onSingleCheckboxChange = (value: string) => {
    // If the same checkbox is clicked and it's already selected, deselect it
    if (field.value === value) {
      field.onChange(null);
    } else {
      // Otherwise, select this checkbox (deselecting any other)
      field.onChange(value);
    }
  };

  const onMultipleValueChange = (value: any) => {
    if (!Array.isArray(field.value)) {
      return field.onChange([value]);
    }
    if (field.value.includes(value)) {
      field.onChange(field.value.filter((v: string) => v !== value));
    } else {
      field.onChange([...field.value, value]);
    }
  };

  const renderedOptions = options.map((option) => {
    const checked =
      multi && Array.isArray(field.value)
        ? field.value.includes(option.value)
        : field.value === option.value;

    return (
      <RHFCheckboxItem
        onChange={multi ? onMultipleValueChange : onSingleCheckboxChange}
        option={option}
        checked={checked}
        key={`${name}-${option.value}`}
        translationNamespace={translationNamespace}
      />
    );
  });

  return (
    <>
      <InlineContainer data-testid={testId} inline={inline}>
        {label && (
          <Typography variant="body1" component="span">
            {label}:
          </Typography>
        )}
        <CheckboxGroup
          role="group"
          aria-label={typeof label === "string" ? label : "Checkbox group"}
        >
          {renderedOptions}
        </CheckboxGroup>
      </InlineContainer>
      {error?.type !== "is-invalid-option" && error?.message && (
        <Error>
          <Typography
            variant="caption"
            color="inherit"
            data-testid="rhf-checkbox-group-error"
          >
            {error.message}
          </Typography>
        </Error>
      )}
    </>
  );
};
